import { SiteStatus } from './constants'
import { FetchSitesParams, SiteItem } from './sitesSlice'
import { getAfterCursor, parameterizeArrayofObjects } from '../../utils/graphqlUtil'
import { LIMIT_DEFAULT } from '../../constants'
import { gql } from 'graphql-request'

export type FetchCompactedSitesParams = {
  datasourceId: string
  pageSize: number
  page: number
  searchQuery?: string
}
export type CompactedSite = {
  id: string
  siteId: string
  displayName: string
  status: SiteStatus
  onLegalHold: boolean
}
export const queryCompactedSites = (params: FetchCompactedSitesParams): string => {
  const { datasourceId, pageSize, page, searchQuery } = params

  const cursor = getAfterCursor(page, pageSize)

  return gql`
    {
      site(
        datasourceIds: ["${datasourceId}"],
        first: ${pageSize}
        after: "${cursor}"
        ${searchQuery ? `searchQuery: "${searchQuery}"` : ''}
      ) {
        count
        edges {
          node {
            id
            siteId
            displayName
            status
            onLegalHold
          }
        }
      }
    }
  `
}
export const mapCompactedSites = (raw: any): { list: CompactedSite[]; total: number } => {
  try {
    let list: CompactedSite[] = []

    if (Array.isArray(raw?.site?.edges)) {
      list = raw.site.edges.map(
        ({
          node: { id = '', siteId = '', displayName = '', status = '', onLegalHold = false }
        }) => {
          return {
            id,
            siteId,
            displayName,
            status,
            onLegalHold
          }
        }
      )
    }

    return {
      list,
      total: raw.site?.count
    }
  } catch (error) {
    console.error('Error mapping compacted sites:', error)
    throw error
  }
}

export type FetchSiteSummaryParams = {
  datasourceId: string
  siteId: string
}
export type SiteSummary = CompactedSite & {
  owners: Array<{
    userPrincipalName: string
  }>
  createdAt: string
  updatedAt: string
}
export const querySiteSummary = (params: FetchSiteSummaryParams): string => {
  const { datasourceId, siteId } = params

  return gql`
      {
        site(
          datasourceIds: ["${datasourceId}"],
          siteId: "${siteId}",
          first: 1
        ) {
          edges {
            node {
              id
              siteId
              displayName
              owners {
                userPrincipalName
              }
              createdDateTime
              status
              lastModifiedDateTime
              onLegalHold
            }
          }
        }
      }
    `
}
export const mapSiteSummary = (raw: any): SiteSummary | undefined => {
  try {
    const edge = raw?.site?.edges?.[0]

    if (!edge || !edge.node) {
      return undefined
    }

    const {
      id = '',
      siteId = '',
      displayName = '',
      owners = [],
      createdDateTime = '',
      lastModifiedDateTime = '',
      status = '',
      onLegalHold = false
    } = edge.node

    return {
      id,
      siteId,
      displayName,
      owners: owners ?? [],
      createdAt: createdDateTime,
      updatedAt: lastModifiedDateTime,
      status,
      onLegalHold
    }
  } catch (error) {
    console.error('Error mapping site summary:', error)
    throw error
  }
}

export const querySites = (params: FetchSitesParams): string => {
  const { datasourceId, page = 1, pageSize = LIMIT_DEFAULT, filters, searchQuery } = params
  const cursor = getAfterCursor(page, pageSize)

  let filterParamsString = ''
  const filterString = filters && filters.filter ? parameterizeArrayofObjects(filters.filter) : ''

  if (filterString) {
    filterParamsString += `, filter: ${filterString}`
  }

  return gql`
    {
      site(
        datasourceIds: ["${datasourceId}"]
        first: ${pageSize}
        after: "${cursor}"
        ${filterParamsString}
        ${
          filters?.booleanFilter?.length
            ? `booleanFilter: ${parameterizeArrayofObjects(filters.booleanFilter)}`
            : ''
        }
        ${searchQuery ? `searchFilter: [{key:SITE_NAME, value:"${searchQuery}"}]` : ''}
      ) {
        count
        edges {
          node {
            id
            siteId
            displayName
            owners {
              userPrincipalName
            }
            status
            lastModifiedDateTime
            onLegalHold
          }
        }
      }
    }
  `
}

export const mapQuerySites = (raw: any): { list: SiteItem[]; total: number } => {
  const sharePointSites: SiteItem[] = raw.site.edges.map(({ node }) => ({
    id: node?.id,
    siteId: node?.siteId,
    displayName: node?.displayName,
    owner: node?.owners?.map(({ userPrincipalName }) => userPrincipalName),
    lastModified: node?.lastModifiedDateTime,
    onLegalHold: node?.onLegalHold,
    status: node?.status
  }))

  return { list: sharePointSites, total: raw.site.count }
}
